"use client";

import { AlertCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { BookResultData } from "@/components/book-result-card";
import {
  type BookData,
  BookSelectModal,
  type ChapterInfo,
  type PageRange,
} from "@/components/book-select-modal";
import { AutoInputForm } from "@/components/citation-tool/auto-input-form";
import { BookSearchForm } from "@/components/citation-tool/book-search-form";
import {
  BulkResultSummary,
  type BulkSummary,
} from "@/components/citation-tool/bulk-result-summary";
import { CitationResult } from "@/components/citation-tool/citation-result";
import { EditDialog } from "@/components/citation-tool/edit-dialog";
import {
  type InputMode,
  InputModeTabs,
} from "@/components/citation-tool/input-mode-tabs";
import { ManualEntryForm } from "@/components/citation-tool/manual-entry-form";
import { OutputLanguageSelect } from "@/components/citation-tool/output-language-select";
import { useToolStrings } from "@/components/citation-tool/tool-strings";
import { WorksSearchForm } from "@/components/citation-tool/works-search-form";
import { CitationWizard } from "@/components/citation-wizard";
import { StudioCta } from "@/components/studio-cta";
import { StyleCombobox } from "@/components/style-combobox";
import { Button } from "@/components/ui/button";
import type { WorkResultData } from "@/components/work-result-card";
import {
  CITATION_STYLES,
  type CitationStyleMeta,
} from "@/data/citation-styles";
import {
  trackBookSearch,
  trackBookSelected,
  trackCitationGenerated,
  trackWizardCompleted,
  trackWizardOpened,
  trackWorkSelected,
  trackWorksSearch,
} from "@/lib/analytics";
import { addCitation } from "@/lib/bibliography";
import { type AutoInputPlan, planAutoInput } from "@/lib/bulk-input";
import { normalizeOutputLocale } from "@/lib/output-locales";
import {
  countMissingFields,
  detectSourceType,
  hasMissingRequiredFields,
  type WizardEntry,
} from "@/lib/source-types";

interface EditableEntry {
  title?: string;
  author?: string;
  issued?: string;
  URL?: string;
  publisher?: string;
  containerTitle?: string;
  type?: string;
  volume?: string;
  issue?: string;
  page?: string;
  DOI?: string;
  ISBN?: string;
}

interface BookResult {
  key: string;
  title: string;
  author: string;
  authorKeys: string[];
  year: number | null;
  isbn: string | null;
  allIsbns: string[];
  coverUrl: string | null;
  coverUrlSmall: string | null;
  coverUrlLarge: string | null;
  publisher: string | null;
  allPublishers: string[];
  pages: number | null;
  subjects: string[];
  languages: string[];
  editionCount: number;
}

// Top-level regex for input type detection
const DOI_REGEX = /^10\.\d/;
const ISBN_REGEX = /^97[89]\d{10}$|^97[89]-/;
const BIBTEX_REGEX = /^@\w+\{/;
// PubMed: bare/`pmid:`-prefixed PMID (≤8 digits), a PMCID, or a pubmed.ncbi.nlm.nih.gov URL.
const PMID_REGEX = /^(?:pmid:\s*)?\d{1,8}$|^PMC\d+$/i;
const PUBMED_URL_REGEX =
  /^https?:\/\/(?:[\w-]+\.)*pubmed\.ncbi\.nlm\.nih\.gov\//i;
// arXiv: `arXiv:`-prefixed or bare current-scheme id (`YYMM.NNNNN` + optional `vN`), or an
// arxiv.org/abs|pdf URL. The mandatory dot keeps it from matching the bare-PMID shape.
const ARXIV_REGEX = /^(?:arxiv:\s*)?\d{4}\.\d{4,5}(?:v\d+)?$/i;
const ARXIV_URL_REGEX = /^https?:\/\/(?:[\w-]+\.)*arxiv\.org\/(?:abs|pdf)\//i;

/** True for something we would have tried to fetch as a web page. */
function looksLikeHttpUrl(input: string): boolean {
  try {
    const { protocol } = new URL(input.trim());
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

function detectInputType(
  input: string
): "doi" | "isbn" | "pmid" | "arxiv" | "bibtex" | "url" {
  if (DOI_REGEX.test(input)) {
    return "doi";
  }
  if (ISBN_REGEX.test(input)) {
    return "isbn";
  }
  if (PMID_REGEX.test(input) || PUBMED_URL_REGEX.test(input)) {
    return "pmid";
  }
  if (ARXIV_REGEX.test(input) || ARXIV_URL_REGEX.test(input)) {
    return "arxiv";
  }
  if (BIBTEX_REGEX.test(input)) {
    return "bibtex";
  }
  return "url";
}

interface CitationToolProps {
  defaultStyle?: string;
  styles?: CitationStyleMeta[];
  onCitationSaved?: () => void;
  /**
   * Value from a `?source=` deep link (used by the "Cite This Page" bookmarklet).
   * When present, it prefills the auto-detect input and runs one citation on mount.
   */
  source?: string;
  /**
   * Which input tab to open on. Defaults to "auto". Source-type landing pages with no
   * URL/identifier lookup (e.g. the Podcast or Movie generator) pass "manual" so the
   * relevant hand-entry form is shown first instead of an auto-detect box that can't
   * resolve them.
   */
  initialMode?: InputMode;
  /**
   * Preselects the manual-entry source type by CSL type (e.g. "broadcast" for a podcast,
   * "motion_picture" for a movie). Only meaningful with `initialMode="manual"`.
   */
  initialManualType?: string;
  /**
   * Example lines for the empty auto-detect box. A landing page for one kind of
   * identifier (the ISBN or DOI generators) passes lines of that kind so its first
   * action reads differently from the homepage's mixed examples.
   */
  autoPlaceholder?: string;
  /**
   * Citation OUTPUT language to start on. Localized pages pass the one matching the page
   * (`/pt` → `pt-BR`), because someone reading in Portuguese more often than not wants
   * "s.d." rather than "n.d.". It is only a starting value — the selector is on screen
   * and unchanged, so an English paper is one click away. English pages pass nothing and
   * keep `en-US`, the byte-identical default path.
   */
  defaultOutputLocale?: string;
  /**
   * Style-locked embeds hide the style combobox so an LMS assignment cannot
   * drift off the style the instructor chose. The locked slug is still the
   * live `defaultStyle`.
   */
  lockStyle?: boolean;
  /** Visible name for the locked style, e.g. "APA". */
  lockStyleLabel?: string;
  /**
   * Rendered inside the Bibliography Studio, which already *is* the destination for
   * saved citations — so the "open the Studio" footer and link are omitted.
   */
  inStudio?: boolean;
}

// Rich-copy HTML is kept for the last few citations shown (style or language switches).
const MAX_REMEMBERED_HTML = 20;

export function CitationTool({
  defaultStyle,
  styles = CITATION_STYLES,
  onCitationSaved,
  source,
  initialMode = "auto",
  initialManualType,
  autoPlaceholder,
  defaultOutputLocale,
  lockStyle = false,
  lockStyleLabel,
  inStudio = false,
}: CitationToolProps) {
  const toolStrings = useToolStrings();
  const validSlug =
    defaultStyle && styles.some((s) => s.slug === defaultStyle)
      ? defaultStyle
      : "apa";

  // Core state
  const [style, setStyle] = useState<string>(validSlug);
  // Citation OUTPUT language (localized CSL terms). English is the default no-op path;
  // a localized page may start somewhere else via `defaultOutputLocale`.
  const [lang, setLang] = useState<string>(
    normalizeOutputLocale(defaultOutputLocale)
  );
  const [citation, setCitation] = useState<string | null>(null);
  // The matching in-text citation for the current result (issue #109). Threaded from the
  // `/api/cite` `inText` field; null hides the in-text line in the result card.
  const [inText, setInText] = useState<string | null>(null);
  const [entry, setEntry] = useState<EditableEntry | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Set when a source page could not be read (bot wall, paywall, dead link, timeout)
  // and the reader can still cite it by hand. Holds the address to prefill so the
  // error is an offer to continue rather than a dead end.
  const [manualFallbackUrl, setManualFallbackUrl] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState(false);

  // Input mode
  const [inputMode, setInputMode] = useState<InputMode>(initialMode);

  // Auto-detect input
  const [autoInput, setAutoInput] = useState("");

  // Bulk identifier paste (multiple lines → one citation per line)
  const [bulkProgress, setBulkProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [bulkSummary, setBulkSummary] = useState<BulkSummary | null>(null);

  // Book search
  const [bookQuery, setBookQuery] = useState("");
  const [bookResults, setBookResults] = useState<BookResult[]>([]);
  const [bookSearchLoading, setBookSearchLoading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);

  // Book selection modal
  const [selectedBook, setSelectedBook] = useState<BookResult | null>(null);
  const [bookModalOpen, setBookModalOpen] = useState(false);
  const [citingBook, setCitingBook] = useState(false);

  // Article (Crossref works) search
  const [worksQuery, setWorksQuery] = useState("");
  const [worksResults, setWorksResults] = useState<WorkResultData[]>([]);
  const [worksSearchLoading, setWorksSearchLoading] = useState(false);
  const [worksTotalResults, setWorksTotalResults] = useState(0);

  // Manual entry
  const [manualEntry, setManualEntry] = useState<EditableEntry>(
    initialManualType ? { type: initialManualType } : {}
  );

  // Wizard
  const [wizardOpen, setWizardOpen] = useState(false);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState<EditableEntry>({});

  // Computed
  const missingFieldsResult = entry
    ? countMissingFields(entry as WizardEntry, detectSourceType(entry.type))
    : null;
  const missingCount = missingFieldsResult?.total ?? 0;

  useEffect(() => {
    if (defaultStyle) {
      setStyle(defaultStyle);
    }
  }, [defaultStyle]);

  // Reset results when switching modes
  useEffect(() => {
    setCitation(null);
    setInText(null);
    setEntry(null);
    setError(null);
    setBookResults([]);
    setTotalResults(0);
    setWorksResults([]);
    setWorksTotalResults(0);
  }, []);

  // One-shot prefill from a `?source=` deep link (used by the "Cite This Page"
  // bookmarklet). The value flows through the same auto-detect path as a manual paste,
  // so a DOI/ISBN/PMID/arXiv ID works too — it is a general "cite this" link, not
  // URL-only. The ref guard ensures exactly one auto-submit, never re-running on
  // re-render. `style` is already initialized from `defaultStyle` via `validSlug`.
  const prefilledRef = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: run exactly once for the initial ?source= value; generateCitation is intentionally not a dependency
  useEffect(() => {
    if (prefilledRef.current) {
      return;
    }
    const trimmed = source?.trim();
    if (!trimmed) {
      return;
    }
    prefilledRef.current = true;
    setInputMode("auto");
    setAutoInput(trimmed);
    generateCitation(trimmed).catch(() => {
      // generateCitation surfaces its own errors via state; ignore the floating rejection.
    });
  }, [source]);

  // HTML renderings the API returned, keyed by the plain citation they belong to, so the
  // result card copies rich text only when it matches the citation on screen.
  const citationHtmlRef = useRef(new Map<string, string>());
  const [, setCitationHtmlVersion] = useState(0);
  function rememberCitationHtml(text: unknown, html: unknown): void {
    if (typeof text !== "string" || typeof html !== "string" || !html) {
      return;
    }
    const known = citationHtmlRef.current;
    if (known.size >= MAX_REMEMBERED_HTML) {
      const oldest = known.keys().next().value;
      if (oldest !== undefined) {
        known.delete(oldest);
      }
    }
    known.set(text, html);
    setCitationHtmlVersion((version) => version + 1);
  }

  // API helpers
  async function fetchCitation(payload: {
    input?: string;
    entry?: EditableEntry;
    style: string;
    lang?: string;
    // Ask the API to also return the matching in-text citation (issue #109). The bulk
    // path omits it so it keeps hitting the bibliography-only cache.
    inText?: boolean;
  }): Promise<{
    ok: boolean;
    data: {
      citation?: string;
      citationHtml?: string;
      entry?: EditableEntry;
      inText?: string;
      error?: string;
      /** `SourceFetchError.kind` when the failure was an unreadable source page. */
      code?: string;
    };
    status: number;
  }> {
    const res = await fetch("/api/cite", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // Thread the selected output language so localized terms come back from the API.
      // An explicit payload.lang (e.g. an instant re-localize) overrides the state value.
      // A single citation also asks for its HTML so "Copy" keeps the italics.
      body: JSON.stringify({
        ...payload,
        lang: payload.lang ?? lang,
        ...(payload.inText ? { html: true } : {}),
      }),
    });
    const data = await res.json();
    rememberCitationHtml(data?.citation, data?.citationHtml);
    return { ok: res.ok, data, status: res.status };
  }

  function handleCiteError(
    status: number,
    errorMsg?: string,
    code?: string,
    input?: string
  ): void {
    setError(
      errorMsg ??
        (status === 429
          ? "Rate limited. Please wait a minute and try again."
          : "Could not generate citation.")
    );
    // Every source-fetch failure except a rate limit is permanent for us but
    // trivial for the reader, who has the page open: offer the manual form with
    // their address already in it. Only the URL is carried over — guessing a
    // title from the path would put unverified text in a citation.
    setManualFallbackUrl(
      code && code !== "rate-limited" && input && looksLikeHttpUrl(input)
        ? input
        : null
    );
  }

  function switchToManualEntry(url: string): void {
    setManualEntry((prev) => ({
      ...prev,
      URL: url,
      type: prev.type ?? "webpage",
    }));
    setInputMode("manual");
    setError(null);
    setManualFallbackUrl(null);
  }

  function setCitationResult(
    citationText: string,
    entryData: EditableEntry | undefined,
    inTextText?: string
  ): void {
    setCitation(citationText);
    setInText(inTextText ?? null);
    if (entryData) {
      setEntry(entryData);
      const sourceType = detectSourceType(entryData.type);
      if (hasMissingRequiredFields(entryData as WizardEntry, sourceType)) {
        setWizardOpen(true);
        trackWizardOpened();
      }
    }
  }

  // Output-language change. Persist the choice; if a citation is already shown and we
  // have its parsed entry, instantly re-localize it from the entry (no network re-lookup)
  // so the new terms appear immediately. Results without an entry just apply on next cite.
  async function handleLangChange(nextLang: string) {
    setLang(nextLang);
    if (!entry) {
      return;
    }
    try {
      const { ok, data } = await fetchCitation({
        entry,
        style,
        lang: nextLang,
        inText: true,
      });
      if (ok && data.citation) {
        setCitation(data.citation);
        setInText(data.inText ?? null);
      }
    } catch {
      // Keep the existing citation if the re-localize request fails.
    }
  }

  // Core citation generation. `trackAs` overrides the auto-detected input type for
  // analytics — used when the input is a DOI we obtained from a search (e.g. articles).
  async function generateCitation(input: string, trackAs?: "works-search") {
    setError(null);
    setManualFallbackUrl(null);
    setCitation(null);
    setInText(null);
    setEntry(null);
    setLoading(true);

    try {
      const { ok, data, status } = await fetchCitation({
        input,
        style,
        inText: true,
      });

      if (!ok) {
        handleCiteError(status, data.error, data.code, input);
        return;
      }

      setCitation(data.citation ?? "");
      setInText(data.inText ?? null);
      trackCitationGenerated(style, trackAs ?? detectInputType(input));

      if (data.entry) {
        setEntry(data.entry);
        const sourceType = detectSourceType(data.entry.type);
        if (hasMissingRequiredFields(data.entry as WizardEntry, sourceType)) {
          setWizardOpen(true);
          trackWizardOpened();
        }
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // Resolve a single line and add the result to the active bibliography. Returns whether
  // the line was successfully cited (so the caller can tally ✓/✗ for the batch summary).
  async function citeBulkLine(
    line: string
  ): Promise<{ ok: boolean; error?: string }> {
    try {
      const { ok, data } = await fetchCitation({ input: line, style });
      if (ok && data.citation) {
        addCitation(data.citation, data.entry ?? {}, style, lang);
        trackCitationGenerated(style, detectInputType(line));
        return { ok: true };
      }
      return { ok: false, error: data.error ?? "Could not generate citation." };
    } catch {
      return { ok: false, error: "Network error." };
    }
  }

  // Bulk paste: resolve each line sequentially (to respect upstream rate limits the route
  // already handles) and add every success to the active bibliography.
  async function runBulkCitations(
    plan: Extract<AutoInputPlan, { kind: "bulk" }>
  ) {
    setError(null);
    setCitation(null);
    setInText(null);
    setEntry(null);
    setBulkSummary(null);
    setLoading(true);
    setBulkProgress({ done: 0, total: plan.lines.length });

    const results: BulkSummary["results"] = [];
    let added = 0;
    for (const line of plan.lines) {
      const outcome = await citeBulkLine(line);
      if (outcome.ok) {
        added += 1;
      }
      results.push({ line, ok: outcome.ok, error: outcome.error });
      setBulkProgress({ done: results.length, total: plan.lines.length });
    }

    setBulkProgress(null);
    setLoading(false);
    setBulkSummary({ results, added, droppedCount: plan.droppedCount });
    if (added > 0) {
      onCitationSaved?.();
    }
  }

  // Auto-detect submit
  async function handleAutoSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = autoInput.trim();
    if (!trimmed) {
      setError("Enter a URL, DOI, ISBN, or paste BibTeX.");
      return;
    }

    const plan = planAutoInput(trimmed);
    if (plan.kind === "bulk") {
      await runBulkCitations(plan);
      return;
    }
    setBulkSummary(null);
    await generateCitation(plan.input);
  }

  // Book search
  async function handleBookSearch(e: React.FormEvent) {
    e.preventDefault();
    const q = bookQuery.trim();
    if (!q) {
      return;
    }

    setError(null);
    setBookSearchLoading(true);
    setBookResults([]);
    setTotalResults(0);
    trackBookSearch(q);

    try {
      const res = await fetch(`/api/search/books?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? "Search failed.");
        return;
      }
      setBookResults(data.results ?? []);
      setTotalResults(data.totalResults ?? 0);
      if ((data.results ?? []).length === 0) {
        setError("No books found. Try a different search term.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setBookSearchLoading(false);
    }
  }

  // Book selection
  function handleSelectBook(book: BookResultData) {
    setSelectedBook(book as BookResult);
    setBookModalOpen(true);
    trackBookSelected(book.title);
  }

  // Article (Crossref works) search
  async function handleWorksSearch(e: React.FormEvent) {
    e.preventDefault();
    const q = worksQuery.trim();
    if (!q) {
      return;
    }

    setError(null);
    setWorksSearchLoading(true);
    setWorksResults([]);
    setWorksTotalResults(0);
    trackWorksSearch(q);

    try {
      const res = await fetch(`/api/search/works?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? "Search failed.");
        return;
      }
      setWorksResults(data.results ?? []);
      setWorksTotalResults(data.totalResults ?? 0);
      if ((data.results ?? []).length === 0) {
        setError("No articles found. Try a different search term.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setWorksSearchLoading(false);
    }
  }

  // Article selection — resolve through the existing, battle-tested DOI path.
  async function handleSelectWork(work: WorkResultData) {
    trackWorkSelected(work.title);
    await generateCitation(work.doi, "works-search");
  }

  // Page range formatting
  function formatPageRange(
    pageRange: PageRange | undefined
  ): string | undefined {
    if (!(pageRange && (pageRange.from || pageRange.to))) {
      return undefined;
    }
    const from = pageRange.from || pageRange.to;
    const to = pageRange.to || pageRange.from;
    return from === to ? from : `${from}-${to}`;
  }

  // Chapter info application
  function applyChapterInfo(
    entryData: EditableEntry,
    bookTitle: string,
    chapter: ChapterInfo | undefined
  ): EditableEntry {
    if (!chapter?.title) {
      return entryData;
    }
    return {
      ...entryData,
      containerTitle: bookTitle,
      title: chapter.title,
      type: "chapter",
    };
  }

  // Cite book from modal
  async function handleCiteBookFromModal(
    book: BookData,
    pageRange?: PageRange,
    chapter?: ChapterInfo
  ) {
    if (!book.isbn) {
      return;
    }

    setCitingBook(true);
    setError(null);
    setCitation(null);
    setInText(null);
    setEntry(null);

    try {
      const pageStr = formatPageRange(pageRange);
      const { ok, data, status } = await fetchCitation({
        input: book.isbn,
        style,
        inText: true,
      });

      if (!ok) {
        handleCiteError(status, data.error);
        return;
      }

      let finalEntry = data.entry as EditableEntry;
      if (pageStr) {
        finalEntry = { ...finalEntry, page: pageStr };
      }
      finalEntry = applyChapterInfo(finalEntry, book.title, chapter);

      if (pageStr || chapter?.title) {
        const update = await fetchCitation({
          entry: finalEntry,
          style,
          inText: true,
        });
        if (update.ok) {
          setCitation(update.data.citation ?? "");
          setInText(update.data.inText ?? null);
          setEntry(finalEntry);
        } else {
          setCitationResult(data.citation ?? "", data.entry, data.inText);
        }
      } else {
        setCitationResult(data.citation ?? "", data.entry, data.inText);
      }

      setBookModalOpen(false);
      trackCitationGenerated(style, "book-search");
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setCitingBook(false);
    }
  }

  // Manual entry submit
  async function handleManualSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!manualEntry.title?.trim()) {
      setError("At least a title is required.");
      return;
    }

    setError(null);
    setLoading(true);
    setCitation(null);
    setInText(null);
    setEntry(null);

    try {
      const { ok, data, status } = await fetchCitation({
        entry: manualEntry,
        style,
        inText: true,
      });

      if (!ok) {
        handleCiteError(status, data.error);
        return;
      }

      setCitation(data.citation ?? "");
      setInText(data.inText ?? null);
      setEntry(manualEntry);
      trackCitationGenerated(style, "manual");
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // Edit dialog
  function openEdit() {
    setEditForm(entry ?? {});
    setEditOpen(true);
  }

  async function handleEditSave(e: React.FormEvent) {
    e.preventDefault();
    setEditSaving(true);

    try {
      const { ok, data } = await fetchCitation({
        entry: editForm,
        style,
        inText: true,
      });

      if (ok) {
        setCitation(data.citation ?? "");
        setInText(data.inText ?? null);
        setEntry(editForm);
        setEditOpen(false);
      }
    } finally {
      setEditSaving(false);
    }
  }

  // Wizard completion
  async function handleWizardComplete(updated: EditableEntry) {
    try {
      const { ok, data } = await fetchCitation({
        entry: updated,
        style,
        inText: true,
      });

      if (ok) {
        setCitation(data.citation ?? "");
        setInText(data.inText ?? null);
        setEntry(updated);
        setWizardOpen(false);
        trackWizardCompleted();
      }
    } catch {
      setError("Failed to update citation.");
    }
  }

  return (
    <div className="space-y-6">
      {/* Style + output-language selectors */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          {lockStyle ? (
            <p className="rounded-lg border border-border bg-muted/40 px-3 py-2 font-medium text-sm">
              {lockStyleLabel ?? style}
            </p>
          ) : (
            <StyleCombobox
              featuredStyles={styles}
              onChange={setStyle}
              value={style}
            />
          )}
        </div>
        <OutputLanguageSelect onChange={handleLangChange} value={lang} />
      </div>

      {/* Input Mode Tabs */}
      <InputModeTabs mode={inputMode} onModeChange={setInputMode} />

      {/* Auto-detect Input */}
      {inputMode === "auto" && (
        <AutoInputForm
          loading={loading}
          onChange={setAutoInput}
          onSubmit={handleAutoSubmit}
          placeholder={autoPlaceholder}
          progressLabel={
            bulkProgress
              ? `Citing ${bulkProgress.done} of ${bulkProgress.total}...`
              : undefined
          }
          value={autoInput}
        />
      )}

      {/* Book Search */}
      {inputMode === "book" && (
        <BookSearchForm
          error={error}
          loading={bookSearchLoading}
          onQueryChange={setBookQuery}
          onSelectBook={handleSelectBook}
          onSubmit={handleBookSearch}
          query={bookQuery}
          results={bookResults as BookResultData[]}
          totalResults={totalResults}
        />
      )}

      {/* Article Search (Crossref) */}
      {inputMode === "search-works" && (
        <WorksSearchForm
          error={error}
          loading={worksSearchLoading}
          onQueryChange={setWorksQuery}
          onSelectWork={handleSelectWork}
          onSubmit={handleWorksSearch}
          query={worksQuery}
          results={worksResults}
          totalResults={worksTotalResults}
        />
      )}

      {/* Manual Entry */}
      {inputMode === "manual" && (
        <ManualEntryForm
          entry={manualEntry}
          loading={loading}
          onChange={setManualEntry}
          onSubmit={handleManualSubmit}
        />
      )}

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-destructive text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="min-w-0 space-y-2">
            <p>{error}</p>
            {manualFallbackUrl && (
              <Button
                className="h-8"
                onClick={() => switchToManualEntry(manualFallbackUrl)}
                size="sm"
                type="button"
                variant="outline"
              >
                {toolStrings.errorEnterManually}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Bulk paste summary */}
      {bulkSummary && <BulkResultSummary summary={bulkSummary} />}

      {/* Citation Result */}
      {citation && (
        <CitationResult
          citation={citation}
          citationHtml={citationHtmlRef.current.get(citation)}
          entry={entry}
          inStudio={inStudio}
          inText={inText}
          lang={lang}
          missingCount={missingCount}
          onCitationSaved={onCitationSaved}
          onOpenEdit={openEdit}
          onOpenWizard={() => setWizardOpen(true)}
          style={style}
        />
      )}

      {/* Every generator on the site feeds the same Studio; say so, with the live count. */}
      {inStudio ? null : <StudioCta variant="bar" />}

      {/* Book Selection Modal */}
      <BookSelectModal
        book={selectedBook as BookData | null}
        loading={citingBook}
        onCite={handleCiteBookFromModal}
        onOpenChange={setBookModalOpen}
        open={bookModalOpen}
      />

      {/* Edit Dialog */}
      <EditDialog
        form={editForm}
        onFormChange={setEditForm}
        onOpenChange={setEditOpen}
        onSubmit={handleEditSave}
        open={editOpen}
        saving={editSaving}
      />

      {/* Citation Wizard */}
      {entry && (
        <CitationWizard
          citation={citation ?? ""}
          entry={entry as WizardEntry}
          onComplete={handleWizardComplete}
          onOpenChange={setWizardOpen}
          open={wizardOpen}
          style={style}
        />
      )}
    </div>
  );
}
